import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Visibility as VisibilityIcon,
  DoNotDisturb as BlockIcon
} from '@mui/icons-material';
import { getLeaderboard, getSessionLeaderboard, LeaderboardUser } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { getAllSessions, Session } from '../../services/sessionService';
import { getTeamLeaderboard, TeamLeaderboardEntry } from '../../services/teamService';

/**
 * All-Time Points tab of `/leaderboard` — the original single-page
 * leaderboard, moved here verbatim (minus the shell and page hero) when the
 * page grew a Ranked tab alongside it. Left untouched on purpose: this is a
 * 400+ line body with a session selector, a team/individual branch and
 * pagination, and the regression risk of "tidying" it outweighs the payoff.
 */
function PointsLeaderboardTab() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [leaderboardData, setLeaderboardData] = useState<LeaderboardUser[]>([]);
  const [teamLeaderboardData, setTeamLeaderboardData] = useState<TeamLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Session related state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  // Get session ID from URL if present
  useEffect(() => {
    const sessionId = searchParams.get('sessionId');
    if (sessionId) {
      setSelectedSessionId(parseInt(sessionId));
    }
  }, [searchParams]);

  // Fetch available sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setSessionsLoading(true);
        const fetchedSessions = await getAllSessions();
        setSessions(fetchedSessions);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchSessions();
  }, []);

  // Update selected session when selectedSessionId changes
  useEffect(() => {
    console.log('Looking for session', selectedSessionId, 'in sessions:', sessions.map(s => ({ id: s.id, title: s.title, enrollmentType: s.enrollmentType })));
    if (selectedSessionId) {
      // Compare as numbers since API might return id as string or number
      const session = sessions.find(s => Number(s.id) === Number(selectedSessionId));
      console.log('Found session:', session);
      setSelectedSession(session || null);
    } else {
      setSelectedSession(null);
    }
  }, [selectedSessionId, sessions]);

  // Determine if we should show team leaderboard
  const isTeamLeaderboard = selectedSession?.enrollmentType === 'team';

  // Fetch leaderboard data based on selected session
  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have a selected session ID but haven't loaded the session details yet, wait
      if (selectedSessionId && !selectedSession && sessions.length > 0) {
        console.log('Session not found in loaded sessions, skipping fetch');
        setLoading(false);
        return;
      }

      console.log('Fetching leaderboard...', {
        selectedSessionId,
        selectedSession: selectedSession ? {
          id: selectedSession.id,
          title: selectedSession.title,
          enrollmentType: selectedSession.enrollmentType
        } : null
      });

      if (selectedSessionId && selectedSession) {
        // Check if this is a team session
        if (selectedSession.enrollmentType === 'team') {
          console.log('Fetching TEAM leaderboard for session', selectedSessionId);
          // Fetch team leaderboard
          const teamData = await getTeamLeaderboard(selectedSessionId);
          console.log('Team leaderboard data:', teamData);
          setTeamLeaderboardData(teamData);
          setLeaderboardData([]);
          setTotalUsers(teamData.length);
        } else {
          console.log('Fetching INDIVIDUAL session leaderboard for session', selectedSessionId);
          // Fetch individual session leaderboard
          const response = await getSessionLeaderboard(selectedSessionId, page + 1, rowsPerPage);
          setLeaderboardData(response.users);
          setTeamLeaderboardData([]);
          setTotalUsers(response.total);
        }
      } else {
        console.log('Fetching OVERALL individual leaderboard');
        // Fetch overall individual leaderboard
        const response = await getLeaderboard(page + 1, rowsPerPage);
        setLeaderboardData(response.users);
        setTeamLeaderboardData([]);
        setTotalUsers(response.total);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedSessionId, selectedSession, sessions]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle session selection. Mutates only the sessionId key so a sibling
  // ?tab= param (this tab lives inside the tabbed /leaderboard host) survives.
  const handleSessionChange = (event: SelectChangeEvent<number | string>) => {
    const value = event.target.value;
    const next = new URLSearchParams(searchParams);

    if (value === 'all') {
      setSelectedSessionId(null);
      next.delete('sessionId');
    } else {
      const sessionId = Number(value);
      setSelectedSessionId(sessionId);
      next.set('sessionId', sessionId.toString());
    }

    setSearchParams(next);
    setPage(0); // Reset to first page
  };

  // Display username or first name with last initial
  const getUserDisplayName = (user: LeaderboardUser) => {
    if (user.username) {
      return user.username;
    } else {
      return `${user.firstName} ${user.lastName.charAt(0)}.`;
    }
  };

  // Format accuracy percentage
  const formatAccuracy = (accuracy: number) => {
    // Make sure accuracy is a number and handle edge cases
    const numAccuracy = typeof accuracy === 'number' ? accuracy : 0;
    return `${numAccuracy.toFixed(1)}%`;
  };

  // Determine if a user can view achievements
  const canViewAchievements = (user: LeaderboardUser) => {
    // Allow users to view their own achievements always
    if (authState.user && authState.user.id === user.id) {
      return true;
    }

    // Otherwise respect user preference
    return user.showAchievements;
  };

  // Navigate to user profile
  const handleViewProfile = (userId: number, showAchievements: boolean) => {
    if (!showAchievements) {
      // If user doesn't allow viewing their achievements, do nothing
      return;
    }

    const url = `/leaderboard/user/${userId}`;

    // Include session ID in the URL if we're viewing a session-specific leaderboard
    if (selectedSessionId) {
      navigate(`${url}?sessionId=${selectedSessionId}`);
    } else {
      navigate(url);
    }
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {isTeamLeaderboard
            ? 'Team rankings based on combined scores of all enrolled team members.'
            : 'See where you rank among other students. Scores are based on problem-solving performance and accuracy.'}
        </Typography>

        {/* Session selector */}
        <FormControl sx={{ minWidth: 250 }} data-demo-id="leaderboard-scope-select">
          <InputLabel id="session-select-label">View Leaderboard</InputLabel>
          <Select
            labelId="session-select-label"
            value={selectedSessionId || 'all'}
            label="View Leaderboard"
            onChange={handleSessionChange}
            disabled={sessionsLoading}
          >
            <MenuItem value="all">Overall (All Sessions)</MenuItem>
            {sessions.map(session => (
              <MenuItem key={session.id} value={session.id}>
                {session.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{isTeamLeaderboard ? 'Team' : 'Student'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Points</TableCell>
                {isTeamLeaderboard ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Team Members</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Members Enrolled</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Problems Solved</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Accuracy</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Achievements</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isTeamLeaderboard ? 5 : 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isTeamLeaderboard ? (
                // Team leaderboard
                teamLeaderboardData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No teams enrolled in this session.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  teamLeaderboardData.map((team) => (
                    <TableRow key={team.teamId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {team.rank <= 3 && (
                            <TrophyIcon
                              sx={{
                                color: team.rank === 1 ? '#FFD700' : team.rank === 2 ? '#C0C0C0' : '#CD7F32',
                                fontSize: 24
                              }}
                            />
                          )}
                          <Typography
                            variant="h6"
                            component="span"
                            sx={{
                              fontWeight: 700,
                              color: team.rank <= 3 ? 'primary.main' : 'inherit'
                            }}
                          >
                            {team.rank}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {team.teamName}
                        </Typography>
                        {team.teamDescription && (
                          <Typography variant="body2" color="text.secondary">
                            {team.teamDescription}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {Number(team.totalScore).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{team.memberCount}</TableCell>
                      <TableCell>{team.membersEnrolled}</TableCell>
                    </TableRow>
                  ))
                )
              ) : (
                // Individual leaderboard
                leaderboardData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No leaderboard data available.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leaderboardData.map((user) => {
                  const isCurrentUser = authState.user && authState.user.id === user.id;
                  const canView = canViewAchievements(user);

                  return (
                    <TableRow
                      key={user.id}
                      hover
                      sx={isCurrentUser ? { bgcolor: (theme) => `${theme.palette.primary.main}10` } : undefined}
                    >
                      <TableCell>
                        <Typography
                          variant="h6"
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: user.rank <= 3 ? 'primary.main' : 'inherit'
                          }}
                        >
                          {user.rank <= 3 && (
                            <TrophyIcon sx={{
                              color: user.rank === 1 ? '#FFD700' : user.rank === 2 ? '#C0C0C0' : '#CD7F32',
                              mr: 1,
                              verticalAlign: 'middle'
                            }} />
                          )}
                          {user.rank}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {getUserDisplayName(user)}
                          {isCurrentUser && (
                            <Chip
                              label="You"
                              size="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold" color="primary.main">
                          {user.totalPoints ? user.totalPoints.toFixed(2) : '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.problemsSolved}</TableCell>
                      <TableCell>{formatAccuracy(user.accuracy)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<TrophyIcon />}
                          label={user.achievementCount}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canView ? (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewProfile(user.id, canView)}
                          >
                            View Trophies
                          </Button>
                        ) : (
                          <Tooltip title="This user has chosen to keep their achievements private">
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<BlockIcon />}
                                disabled
                              >
                                Private
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default PointsLeaderboardTab;
